/**
 * Hook for Onboarding Readiness Verification
 *
 * Wraps the `verifyOnboardingReady` callable (Wave 2 backend) which executes
 * the 8 readiness oracles (4 login + 4 dashboard) and returns an aggregate
 * pass/fail with per-oracle detail.
 *
 * Mirrors the shape of `useSourceVerification` (loading/error/result + run).
 *
 * @see services/admin-functions/src/verification/onboardingReadinessTypes.ts
 */

import { useState, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';
import type {
  OnboardingReadinessResult,
  VerifyOnboardingReadyInput,
} from '../types/onboardingReadiness';

export interface UseOnboardingReadinessReturn {
  result: OnboardingReadinessResult | null;
  loading: boolean;
  error: string | null;
  run: (companyId: string) => Promise<OnboardingReadinessResult | null>;
  reset: () => void;
}

export function useOnboardingReadiness(): UseOnboardingReadinessReturn {
  const [result, setResult] = useState<OnboardingReadinessResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(
    async (companyId: string): Promise<OnboardingReadinessResult | null> => {
      setLoading(true);
      setError(null);

      try {
        const fn = httpsCallable<VerifyOnboardingReadyInput, OnboardingReadinessResult>(
          functions,
          'verifyOnboardingReady'
        );
        const response = await fn({ companyId });
        const data = response.data;
        setResult(data);
        return data;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'verifyOnboardingReady failed';
        console.error('[useOnboardingReadiness] error:', err);
        setError(msg);
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const reset = useCallback(() => {
    setResult(null);
    setError(null);
  }, []);

  return { result, loading, error, run, reset };
}
